// Iter 386 — Detalle de un asiento de reclasificación (entradas Debe/Haber por cuenta y OT + bills incluidas).
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const fmtCLP = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;

function descargarCSV(nombre, filas, cols) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [cols.join(';'), ...filas.map((f) => cols.map((c) => esc(f[c])).join(';'))].join('\n');
  const url = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = nombre; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export function AsientoDetalleDialog({ asientoId, onClose }) {
  const [doc, setDoc] = useState(null);
  const [tab, setTab] = useState('entradas');

  useEffect(() => {
    if (!asientoId) return;
    setDoc(null);
    api.get(`/contabilidad/cuentas-alegra/asientos/${asientoId}`).then((r) => setDoc(r.data)).catch((err) => {
      toast.error(err?.response?.data?.detail || 'No se pudo cargar el asiento');
      onClose();
    });
  }, [asientoId, onClose]);

  const debe = (doc?.entradas || []).reduce((s, e) => s + (e.debe || 0), 0);
  const haber = (doc?.entradas || []).reduce((s, e) => s + (e.haber || 0), 0);

  return (
    <Dialog open={!!asientoId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="dialog-asiento-detalle">
        <DialogHeader>
          <DialogTitle className="text-base">
            Asiento de reclasificación {doc?.periodo} · {doc?.referencia}
            {doc?.alegra_journal_id && <Badge variant="outline" className="ml-2 text-[10px] text-emerald-700 border-emerald-300">Alegra #{doc.alegra_journal_number || doc.alegra_journal_id}</Badge>}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {doc ? <>Fecha {fmtFecha(doc.fecha)} · {doc.n_bills} facturas pagadas · neto {fmtCLP(doc.total_neto)} · Debe {fmtCLP(debe)} = Haber {fmtCLP(haber)}{doc.comentario_aprobacion ? ` · Aprobación: ${doc.comentario_aprobacion}` : ''}</> : 'Cargando…'}
          </DialogDescription>
        </DialogHeader>
        {!doc && <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>}
        {doc && (
          <>
            <div className="flex items-center gap-2 text-xs flex-wrap" data-testid="asiento-tabs">
              {[['entradas', `Entradas (${doc.entradas.length})`], ['bills', `Facturas (${doc.bills.length})`], ['omitidas', `Omitidas (${(doc.omitidas || []).length})`]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setTab(k)} data-testid={`asiento-tab-${k}`}
                  className={`rounded-full border px-2.5 py-1 ${tab === k ? 'bg-slate-800 text-white border-slate-800' : 'bg-white hover:bg-slate-50'}`}>{l}</button>
              ))}
              <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs" data-testid="btn-csv-asiento"
                onClick={() => tab === 'bills'
                  ? descargarCSV(`asiento_${doc.periodo}_facturas.csv`, doc.bills.map((b) => ({ ...b, fecha: fmtFecha(b.fecha), cuentas: b.lineas.map((l) => `${l.cuenta_nombre}: ${l.neto}`).join(' | ') })), ['folio', 'proveedor', 'rut', 'fecha', 'total_bill', 'neto_reclasificado', 'cost_center_nombre', 'cuentas'])
                  : descargarCSV(`asiento_${doc.periodo}_entradas.csv`, doc.entradas, ['cuenta_id', 'cuenta_nombre', 'cost_center_nombre', 'debe', 'haber'])}>
                <Download className="w-3.5 h-3.5 mr-1" /> CSV
              </Button>
            </div>
            <div className="border rounded max-h-[55vh] overflow-y-auto">
              {tab === 'entradas' && (
                <table className="w-full text-xs" data-testid="tabla-entradas">
                  <thead className="bg-muted sticky top-0"><tr>
                    <th className="text-left px-2 py-1.5">Cuenta</th><th className="text-left px-2 py-1.5">Centro de costo (OT)</th>
                    <th className="text-right px-2 py-1.5">Debe</th><th className="text-right px-2 py-1.5">Haber</th></tr></thead>
                  <tbody>
                    {doc.entradas.map((e, i) => (
                      <tr key={i} className={`border-t ${e.haber ? 'bg-amber-50/40' : ''}`}>
                        <td className="px-2 py-1">{e.cuenta_nombre} <span className="text-muted-foreground font-mono">#{e.cuenta_id}</span></td>
                        <td className="px-2 py-1">{e.cost_center_nombre || <span className="text-muted-foreground">— sin OT</span>}</td>
                        <td className="px-2 py-1 text-right font-mono">{e.debe ? fmtCLP(e.debe) : ''}</td>
                        <td className="px-2 py-1 text-right font-mono">{e.haber ? fmtCLP(e.haber) : ''}</td>
                      </tr>
                    ))}
                    <tr className="border-t font-bold bg-slate-50"><td className="px-2 py-1.5" colSpan={2}>Totales</td>
                      <td className="px-2 py-1.5 text-right font-mono">{fmtCLP(debe)}</td><td className="px-2 py-1.5 text-right font-mono">{fmtCLP(haber)}</td></tr>
                  </tbody>
                </table>
              )}
              {tab === 'bills' && (
                <table className="w-full text-xs" data-testid="tabla-bills-asiento">
                  <thead className="bg-muted sticky top-0"><tr>
                    <th className="text-left px-2 py-1.5">Folio</th><th className="text-left px-2 py-1.5">Proveedor</th><th className="text-left px-2 py-1.5">Fecha</th>
                    <th className="text-left px-2 py-1.5">OT</th><th className="text-right px-2 py-1.5">Neto reclas.</th><th className="text-left px-2 py-1.5">Cuentas destino</th></tr></thead>
                  <tbody>
                    {doc.bills.map((b) => (
                      <tr key={b.alegra_id} className="border-t">
                        <td className="px-2 py-1 font-mono">{b.folio}</td>
                        <td className="px-2 py-1 truncate max-w-[200px]" title={b.proveedor}>{b.proveedor}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{fmtFecha(b.fecha)}</td>
                        <td className="px-2 py-1 truncate max-w-[160px]" title={b.cost_center_nombre}>{b.cost_center_nombre || '—'}</td>
                        <td className="px-2 py-1 text-right font-mono">{fmtCLP(b.neto_reclasificado)}</td>
                        <td className="px-2 py-1 text-muted-foreground truncate max-w-[260px]" title={b.lineas.map((l) => `${l.cuenta_nombre}: ${fmtCLP(l.neto)}`).join('\n')}>
                          {[...new Set(b.lineas.map((l) => l.cuenta_nombre))].join(', ')}
                          {b.lineas_sin_cuenta?.length > 0 && <span className="text-amber-700"> · {b.lineas_sin_cuenta.length} línea(s) quedan en 5124</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {tab === 'omitidas' && (
                <table className="w-full text-xs" data-testid="tabla-omitidas-asiento">
                  <thead className="bg-muted sticky top-0"><tr><th className="text-left px-2 py-1.5">Folio</th><th className="text-left px-2 py-1.5">Proveedor</th><th className="text-left px-2 py-1.5">Motivo</th></tr></thead>
                  <tbody>
                    {(doc.omitidas || []).length === 0 && <tr><td className="px-2 py-3 text-muted-foreground" colSpan={3}>Sin omitidas</td></tr>}
                    {(doc.omitidas || []).map((o) => (
                      <tr key={o.alegra_id} className="border-t"><td className="px-2 py-1 font-mono">{o.folio}</td><td className="px-2 py-1">{o.proveedor}</td><td className="px-2 py-1 text-muted-foreground">{o.motivo}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
